import React, { useState, useEffect } from 'react'
import { Modal, VStack, HStack } from '@musinsa/mwms-ds'
import { Icon } from '@musinsa/pbo-assets'
import { Spinner } from '@/components/content/Loader'
import Text from '@/components/content/Text'
import { colorSystem } from '@/styles/theme'
import styled from 'styled-components'
import MToast from '@/utils/modal/MToast'
import Button from '@/components/content/Button'
import MAlert from '@/utils/modal/MAlert'
import { Utils } from '@/utils/Utils'
import { AxiosError } from 'axios'
import { EXCEL_UPLOAD_ERROR_CODES, ExcelUploadErrorCode } from '@/services/settlement/settlementManagement/api'

interface ExcelUploadModalProps {
  initialFile: File
  onClose: () => void
  onValidate: (file: File) => Promise<ApiResponseV2<boolean>>
  onUpload: (file: File) => Promise<void>
  onRetry: () => void
  onDownloadTemplate: () => void
}

type UploadState = ExcelUploadErrorCode | 'UPLOADING' | 'SUCCESS'

interface ModalActionButtonsProps {
  onClose: () => void
  primaryButton: {
    label: string
    onClick?: () => void
    disabled?: boolean
    minWidth?: number
  }
}

function ModalActionButtons({ onClose, primaryButton }: ModalActionButtonsProps) {
  const onCancel = () => {
    MToast.error('파일 업로드가 취소되었습니다.')
    onClose()
  }
  return (
    <HStack gap={8}>
      <Button size="md" style={{ minWidth: 74 }} kind="secondary" content="취소" onClick={onCancel} />
      <Button
        size="md"
        style={{ minWidth: primaryButton.minWidth || 74 }}
        kind={primaryButton.disabled ? 'secondary' : 'primary_light'}
        content={primaryButton.label}
        onClick={primaryButton.onClick}
        disabled={primaryButton.disabled}
      />
    </HStack>
  )
}

function ExcelUploadModal({ initialFile, onClose, onValidate, onUpload, onRetry, onDownloadTemplate }: ExcelUploadModalProps) {
  const [uploadState, setUploadState] = useState<UploadState>('UPLOADING')
  const [failedRows, setFailedRows] = useState<string>()

  const handleValidation = async (file: File) => {
    setUploadState('UPLOADING')

    try {
      const response = await onValidate(file)

      if (response.meta.result === 'SUCCESS') {
        setUploadState('SUCCESS')
      }
    } catch (e: unknown) {
      const error = e as AxiosError<ApiResponseV2>
      const response = error.response?.data as ApiResponseV2

      if (response.meta.errorCode === EXCEL_UPLOAD_ERROR_CODES.EXCEL_PARTIAL_VALIDATION_FAILURE) {
        setFailedRows(response.meta.message)
        setUploadState(EXCEL_UPLOAD_ERROR_CODES.EXCEL_PARTIAL_VALIDATION_FAILURE)
        return
      }

      if (response.meta.errorCode === EXCEL_UPLOAD_ERROR_CODES.EXCEL_TOTAL_VALIDATION_FAILURE) {
        setUploadState(EXCEL_UPLOAD_ERROR_CODES.EXCEL_TOTAL_VALIDATION_FAILURE)
        return
      }

      if (response.meta.errorCode === EXCEL_UPLOAD_ERROR_CODES.EXCEL_ROW_COUNT_LIMIT_EXCEEDED) {
        setUploadState(EXCEL_UPLOAD_ERROR_CODES.EXCEL_ROW_COUNT_LIMIT_EXCEEDED)
        return
      }

      if (response.meta.errorCode === EXCEL_UPLOAD_ERROR_CODES.INVALID_EXCEL_FILE) {
        setUploadState(EXCEL_UPLOAD_ERROR_CODES.INVALID_EXCEL_FILE)
        return
      }
      onClose()

      MAlert.show(`일시적인 오류로 처리에 실패했어요.\n잠시 후 다시 시도해주세요.`, {
        subContent: '반복적으로 실패할 경우, 관리자에게 문의해 주세요.',
      })
    }
  }

  const handleRegister = async () => {
    try {
      setUploadState('UPLOADING')
      await onUpload(initialFile)

      MToast.show('기타조정금액이 일괄 등록되었습니다.')
      onClose()
    } catch (e) {
      MAlert.show(Utils.getApiErrMessage(e as AxiosError<ApiResponseV2>, '기타조정금액이 일괄 등록되지 않았습니다. 개발팀에 문의해주세요.'))
    }
  }

  useEffect(() => {
    if (initialFile) {
      handleValidation(initialFile)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleCopyFailedRows = () => {
    if (failedRows) {
      navigator.clipboard.writeText(failedRows)
      MToast.show('실패 행 번호를 복사했습니다.')
    }
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round(bytes / k ** i) + sizes[i]
  }
  const renderContent = () => {
    switch (uploadState) {
      case 'UPLOADING':
        return (
          <ContentWrapper>
            <LoadingBox>
              <Spinner />
              <Text kind="Body_14_regular">파일을 업로드 중이에요.</Text>
            </LoadingBox>
            <ModalActionButtons onClose={onClose} primaryButton={{ label: '등록', disabled: true }} />
          </ContentWrapper>
        )

      case 'SUCCESS':
        return (
          <ContentWrapper>
            <TitleWrapper>
              <Text kind="Headline_20_bold" style={{ textAlign: 'center' }}>
                아래 파일을 각 업체에 등록할까요?
              </Text>
              <Text kind="Body_14_regular" color={colorSystem.text.common.secondary}>
                등록 후 일괄 취소가 불가하니, 파일을 한번 더 확인해 주세요.
              </Text>
            </TitleWrapper>

            <FileInfoBox>
              <HStack gap={16} align="center" style={{ padding: '16px' }}>
                <IconWrapper>
                  <Icon name="ic-paper" size={24} />
                </IconWrapper>
                <VStack gap={2} style={{ flex: 1, minWidth: 0 }}>
                  <Text kind="Body_14_regular">{initialFile.name}</Text>
                  <Text kind="Body_12_regular" color={colorSystem.text.common.secondary}>
                    {initialFile.size && formatFileSize(initialFile.size)}
                  </Text>
                </VStack>
                <Button fit size="sm" kind="secondary" content="변경" onClick={onRetry} />
              </HStack>
            </FileInfoBox>

            <ModalActionButtons onClose={onClose} primaryButton={{ label: '등록', onClick: handleRegister }} />
          </ContentWrapper>
        )

      case EXCEL_UPLOAD_ERROR_CODES.EXCEL_PARTIAL_VALIDATION_FAILURE:
        return (
          <ContentWrapper>
            <TitleWrapper>
              <Text kind="Headline_20_bold" style={{ whiteSpace: 'pre-line', textAlign: 'center' }}>
                파일 업로드에 실패했어요.
                <br />
                실패한 행을 확인하고 수정하여 재업로드 해주세요.
              </Text>
              <Text kind="Body_14_regular" color={colorSystem.text.common.secondary}>
                입력값을 가이드에 맞게 작성하고, 정산 마감 업체는 제외해 주세요.
              </Text>
            </TitleWrapper>

            <FailedRowsBox>
              <HStack gap={4} align="flex-start" style={{ padding: '16px' }}>
                <HStack gap={16} align="flex-start" style={{ flex: 1, minWidth: 0 }}>
                  <Text kind="Body_14_regular" color={colorSystem.text.common.primary} style={{ flexShrink: 0 }}>
                    실패 행
                  </Text>
                  <FailedRowsText kind="Body_14_medium" style={{ maxHeight: '148px', overflow: 'auto', alignItems: 'flex-start' }}>
                    {failedRows}
                  </FailedRowsText>
                </HStack>
                <CopyButton onClick={handleCopyFailedRows}>
                  <Icon name="ic-copy" size={16} color={colorSystem.icon.secondary} />
                </CopyButton>
              </HStack>
            </FailedRowsBox>

            <ModalActionButtons onClose={onClose} primaryButton={{ label: '재업로드', onClick: onRetry, minWidth: 100 }} />
          </ContentWrapper>
        )

      case EXCEL_UPLOAD_ERROR_CODES.INVALID_EXCEL_FILE:
        return (
          <ContentWrapper>
            <TitleWrapper>
              <Text kind="Headline_20_bold" style={{ textAlign: 'center' }}>
                업로드 양식에 맞지 않는 파일이에요.
              </Text>
              <Text kind="Body_14_regular" color={colorSystem.text.common.secondary}>
                템플릿을 다운받아 양식에 맞춰 다시 업로드해 주세요.
              </Text>
            </TitleWrapper>

            <FileInfoBox>
              <HStack gap={16} align="center" style={{ padding: '16px' }}>
                <IconWrapper>
                  <Icon name="ic-paper" size={24} />
                </IconWrapper>
                <VStack gap={2} style={{ flex: 1, minWidth: 0 }}>
                  <Text kind="Body_14_regular">기타조정비 일괄 등록 템플릿</Text>
                </VStack>
                <Button fit size="sm" kind="secondary" content="다운로드" onClick={onDownloadTemplate} />
              </HStack>
            </FileInfoBox>

            <ModalActionButtons onClose={onClose} primaryButton={{ label: '재업로드', onClick: onRetry, minWidth: 100 }} />
          </ContentWrapper>
        )

      case EXCEL_UPLOAD_ERROR_CODES.EXCEL_ROW_COUNT_LIMIT_EXCEEDED:
        return (
          <ContentWrapper>
            <TitleWrapper>
              <Text kind="Headline_20_bold" style={{ whiteSpace: 'pre-line', textAlign: 'center' }}>
                한 번에 최대 1,000건까지 업로드 가능해요.
                <br />
                파일을 분할하거나 건수를 줄여 재업로드해 주세요.
              </Text>
            </TitleWrapper>

            <ModalActionButtons onClose={onClose} primaryButton={{ label: '재업로드', onClick: onRetry, minWidth: 100 }} />
          </ContentWrapper>
        )

      case EXCEL_UPLOAD_ERROR_CODES.EXCEL_TOTAL_VALIDATION_FAILURE:
      default:
        return (
          <ContentWrapper>
            <TitleWrapper>
              <Text kind="Headline_20_bold" style={{ whiteSpace: 'pre-line', textAlign: 'center' }}>
                모든 행에 오류가 있어 업로드에 실패했어요.
                <br />
                엑셀 파일을 수정하여 재업로드 해주세요.
              </Text>
              <Text kind="Body_14_regular" color={colorSystem.text.common.secondary}>
                입력값을 가이드에 맞게 작성하고, 정산 마감 업체는 제외해 주세요.
              </Text>
            </TitleWrapper>

            <ModalActionButtons onClose={onClose} primaryButton={{ label: '재업로드', onClick: onRetry, minWidth: 100 }} />
          </ContentWrapper>
        )
    }
  }

  return (
    <Modal open onClose={onClose}>
      {renderContent()}
    </Modal>
  )
}

const ContentWrapper = styled(VStack)`
  margin: 8px 0px;
  gap: 24px;
  align-items: center;
`

const TitleWrapper = styled(VStack)`
  gap: 8px;
  align-items: center;
  width: 416px;
`

const LoadingBox = styled(VStack)`
  width: 100%;
  padding: 32px 0;
  gap: 16px;
  align-items: center;
  justify-content: center;
  background-color: ${colorSystem.bg.filter};
  border-radius: 8px;
`

const FileInfoBox = styled.div`
  width: 100%;
  background-color: ${colorSystem.bg.filter};
  border-radius: 8px;
`

const IconWrapper = styled.div`
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background-color: ${colorSystem.bg.common.secondary};
  border: 1px solid ${colorSystem.border.element};
  border-radius: 6px;
  color: ${colorSystem.text.common.primary};
`

const FailedRowsBox = styled.div`
  width: 100%;
  background-color: ${colorSystem.bg.filter};
  border-radius: 8px;
  overflow: hidden;
`

const FailedRowsText = styled(Text)`
  flex: 1;
  min-width: 0;
  overflow-wrap: break-word;
  word-break: break-word;
`

const CopyButton = styled.button`
  min-width: 16px;
  height: 20px;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  color: ${colorSystem.text.common.secondary};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &:hover {
    color: ${colorSystem.text.common.primary};
  }

  i {
    font-size: 16px;
  }
`

export default ExcelUploadModal
